/**
 * postMessage envelope schema for the auth-origin <-> opener handshake.
 *
 * Spec: docs/superpowers/specs/2026-05-18-passkey-shared-login-design.md §8.2
 *
 * Every cross-origin message between the auth-origin popup and the opener
 * window is wrapped in this envelope so we can:
 *   - Tell apart our own messages from unrelated cross-origin chatter
 *     (`ns: 'updatecore.auth'`)
 *   - Version the protocol cleanly (`protocol: 1`)
 *   - Bind each ceremony to a request id generated by the opener
 *     (`request_id`) — defends against replay of stale messages from a
 *     prior ceremony in the same tab.
 *
 * The full set of guards on the OPENER side is:
 *   1. event.origin === 'https://login.update-fitness.ch'
 *   2. event.source === popupRef
 *   3. envelope.request_id === expected_request_id
 *
 * The POPUP side mirrors guards 1 and 3 when receiving an opener->popup
 * `abort` message; the popup additionally verifies event.origin matches the
 * return_origin it was launched with.
 */

export type EnvelopeType = 'ready' | 'success' | 'cancel' | 'error' | 'abort';

export type EnvelopeBase = {
  ns: 'updatecore.auth';
  protocol: 1;
  request_id: string;
  type: EnvelopeType;
};

export type EnvelopeReady = EnvelopeBase & { type: 'ready' };

export type EnvelopeSuccess = EnvelopeBase & {
  type: 'success';
  sso_ticket?: string;
  credential_id?: string;
};

export type EnvelopeCancel = EnvelopeBase & {
  type: 'cancel';
  reason: 'user_closed' | 'user_denied' | 'timeout';
};

export type EnvelopeError = EnvelopeBase & {
  type: 'error';
  code: string;
  message: string;
};

export type EnvelopeAbort = EnvelopeBase & { type: 'abort' };

export type Envelope =
  | EnvelopeReady
  | EnvelopeSuccess
  | EnvelopeCancel
  | EnvelopeError
  | EnvelopeAbort;

export type BuildEnvelopeArgs =
  | { type: 'ready'; request_id: string }
  | { type: 'success'; request_id: string; sso_ticket?: string; credential_id?: string }
  | {
      type: 'cancel';
      request_id: string;
      reason: 'user_closed' | 'user_denied' | 'timeout';
    }
  | { type: 'error'; request_id: string; code: string; message: string }
  | { type: 'abort'; request_id: string };

/**
 * Wraps a typed envelope payload with the shared ns/protocol header so the
 * caller doesn't have to repeat them at every send site.
 *
 * @param args type-discriminated envelope payload + request_id
 * @returns ready-to-send envelope
 */
export function build_envelope(args: BuildEnvelopeArgs): Envelope {
  return { ns: 'updatecore.auth', protocol: 1, ...args };
}

/**
 * Type-guard that validates a raw `event.data` value is a well-formed
 * envelope from this protocol AND that it belongs to the request the caller
 * is currently waiting on. Returns false on any mismatch — the caller
 * should `return` early on receiving an invalid envelope.
 *
 * @param value             raw event.data (untrusted)
 * @param expected_request_id the request_id the caller generated when launching the popup
 */
export function is_valid_envelope(value: unknown, expected_request_id: string): value is Envelope {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (v['ns'] !== 'updatecore.auth') return false;
  if (v['protocol'] !== 1) return false;
  if (typeof v['request_id'] !== 'string') return false;
  if (v['request_id'] !== expected_request_id) return false;
  if (typeof v['type'] !== 'string') return false;
  if (
    v['type'] !== 'ready' &&
    v['type'] !== 'success' &&
    v['type'] !== 'cancel' &&
    v['type'] !== 'error' &&
    v['type'] !== 'abort'
  ) {
    return false;
  }
  return true;
}
